// CommandPalette — the ⌘K search surface (design brief: one search over people,
// addresses, agreements). Debounced searchAll(), keyboard-first selection,
// navigates via each result's server-provided href.

import { useEffect, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAll, type SearchResult } from '@/lib/commandCenterClient';
import { OcLoading } from '@/components/oc/bits';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Fresh slate every time the palette opens (state persists while mounted-closed).
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setLoading(false);
      setFailed(false);
      setActiveIndex(0);
    }
  }, [open]);

  // Escape closes regardless of where focus sits (input, result button, scrim).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Debounced search — 200 ms, queries of 2+ trimmed chars only. The stale flag
  // drops responses that land after the query moved on (or the palette closed).
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      setFailed(false);
      return;
    }
    setLoading(true);
    setFailed(false);
    let stale = false;
    const timer = window.setTimeout(() => {
      searchAll(q)
        .then((res) => {
          if (stale) return;
          setResults(res.results);
          setActiveIndex(0);
          setLoading(false);
        })
        .catch(() => {
          if (stale) return;
          setResults([]);
          setFailed(true);
          setLoading(false);
        });
    }, 200);
    return () => {
      stale = true;
      window.clearTimeout(timer);
    };
  }, [open, query]);

  if (!open) return null;

  const go = (result: SearchResult) => {
    navigate(result.href);
    onClose();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (results.length > 0) setActiveIndex((i) => (i + 1) % results.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (results.length > 0) setActiveIndex((i) => (i - 1 + results.length) % results.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const active = results[activeIndex];
      if (active) go(active);
    }
  };

  return (
    <div
      className="oc-palette-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="oc-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onKeyDown={onKeyDown}
      >
        <input
          autoFocus
          type="text"
          placeholder="Search people, addresses, agreements…"
          aria-label="Search people, addresses, agreements"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="oc-palette-results">
          {loading && <OcLoading label="Searching…" />}
          {!loading && failed && <div className="oc-empty">Search unavailable</div>}
          {!loading && !failed && results.length === 0 && query.trim().length >= 2 && (
            <div className="oc-empty">No matches.</div>
          )}
          {!loading &&
            !failed &&
            results.map((r, i) => (
              <button
                key={`${r.kind}:${r.href}:${i}`}
                type="button"
                className="oc-palette-item"
                data-active={i === activeIndex ? 'true' : undefined}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => go(r)}
              >
                <span className="oc-palette-kind">{r.kind.toUpperCase()}</span>
                <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{r.label}</span>
                {r.sub != null && <span className="oc-palette-sub">{r.sub}</span>}
              </button>
            ))}
        </div>
        <div className="oc-palette-hint">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
